import {
    format,
    formatInSpecificTimeZone,
    ISO_DATE_ONLY_FORMAT,
    isoDateToLocalTimezoneDate,
} from '@fruitful/fruitils';
import { fromZonedTime } from 'date-fns-tz';
import { Calendar, type CalendarProps } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import type { ColumnEditorOptions } from 'primereact/column';
import {
    Dropdown,
    type DropdownChangeEvent,
    type DropdownProps,
} from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import {
    MultiSelect,
    type MultiSelectChangeEvent,
    type MultiSelectProps,
} from 'primereact/multiselect';
import React from 'react';

interface TextEditorProps {
    options:
        | ColumnEditorOptions
        | { value: string | null; editorCallback: (value: string) => void };
}

export const TextEditor = ({ options }: TextEditorProps) => {
    return (
        <InputText
            type="text"
            style={{ width: '100%', fontSize: '0.85rem' }}
            value={(options.value ?? '') as string}
            onChange={(e) => {
                options.editorCallback?.(e.target.value);
            }}
            onKeyDown={(e) => {
                e.stopPropagation();
            }}
            // nah its ok
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
        />
    );
};

export const TextAreaEditor = ({ options }: TextEditorProps) => {
    return (
        <InputTextarea
            value={(options.value ?? '') as string}
            onChange={(e) => options.editorCallback?.(e.target.value)}
            style={{ width: '100%', fontSize: '0.85rem' }}
            rows={3}
        />
    );
};

export const AddNewTextEditor = ({
    value,
    onChange,
}: {
    value: string;
    onChange: (value: string) => void;
}) => {
    return (
        <InputText
            type="text"
            style={{ width: '100%', fontSize: '0.85rem' }}
            value={value}
            onChange={(e) => {
                onChange(e.target.value);
            }}
            onKeyDown={(e) => {
                e.stopPropagation();
            }}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
        />
    );
};

interface DropdownEditorProps {
    options: ColumnEditorOptions;
    dropdownProps?: DropdownProps;
}

export const DropdownEditor = ({
    options,
    dropdownProps,
}: DropdownEditorProps) => {
    const value = options.value as string | null;

    return (
        <Dropdown
            value={value}
            onChange={(e: DropdownChangeEvent) => {
                options.editorCallback?.(e.value ?? null);
            }}
            style={{
                transform: 'scale(0.85)',
            }}
            panelStyle={{
                fontSize: '0.75rem',
            }}
            variant="filled"
            checkmark={true}
            {...dropdownProps}
        />
    );
};

export interface MultiSelectOption {
    label: string;
    value: string;
}

interface MultiSelectEditorProps {
    options: ColumnEditorOptions;
    multiSelectProps?: MultiSelectProps;
}

export const MultiSelectEditor = ({
    options,
    multiSelectProps,
}: MultiSelectEditorProps) => {
    const value = options.value as string[] | null;

    return (
        <MultiSelect
            value={value}
            showClear={false}
            showSelectAll={false}
            onChange={(e: MultiSelectChangeEvent) => {
                options.editorCallback?.(e.value ?? null);
            }}
            scrollHeight="200px"
            style={{
                transform: 'scale(0.85)',
            }}
            panelStyle={{
                fontSize: '0.75rem',
            }}
            variant="filled"
            {...multiSelectProps}
        />
    );
};

interface CheckboxEditorProps {
    options: ColumnEditorOptions;
}

export const CheckboxEditor = ({ options }: CheckboxEditorProps) => {
    const value = (options.value ?? false) as boolean;

    return (
        <Checkbox
            checked={value}
            onChange={(e) => {
                options.editorCallback?.(e.checked);
            }}
        />
    );
};

interface DateTextEditorProps {
    options: ColumnEditorOptions;
    calendarProps?: Partial<CalendarProps>;
    /** IANA timezone whose calendar day is shown and stored. Defaults to UTC. */
    timeZone?: string;
    /** Clock time to persist in `timeZone` when saving. Defaults to midnight. */
    timeOfDay?: string;
}

/**
 * Parse a date string as a local date, preserving the calendar day in `timeZone`.
 */
const parseDateAsLocal = (dateString: string, timeZone: string): Date => {
    return isoDateToLocalTimezoneDate(
        formatInSpecificTimeZone(dateString, timeZone, ISO_DATE_ONLY_FORMAT),
    );
};

/**
 * Convert a Date to an ISO string, preserving the local calendar day in
 * `timeZone` at `timeOfDay`.
 */
const formatDateForStorage = (
    date: Date,
    timeZone: string,
    timeOfDay: string,
): string => {
    return fromZonedTime(
        `${format(date, ISO_DATE_ONLY_FORMAT)}T${timeOfDay}`,
        timeZone,
    ).toISOString();
};

export const DateTextEditor = ({
    options,
    calendarProps,
    timeZone = 'UTC',
    timeOfDay = '00:00:00',
}: DateTextEditorProps) => {
    const value = (options.value as string)
        ? parseDateAsLocal(options.value as string, timeZone)
        : null;

    return (
        <Calendar
            value={value}
            onChange={(e) => {
                options.editorCallback?.(
                    e.value
                        ? formatDateForStorage(e.value, timeZone, timeOfDay)
                        : null,
                );
            }}
            inputStyle={{ width: '100%', fontSize: '0.85rem' }}
            selectionMode="single"
            showButtonBar
            {...calendarProps}
        />
    );
};

interface AmountEditorProps {
    options: ColumnEditorOptions;
}

export const AmountEditor = ({ options }: AmountEditorProps) => {
    return (
        <InputNumber
            inputStyle={{ width: '100%', fontSize: '0.85rem' }}
            value={options.value as number | null | undefined}
            roundingMode="ceil"
            onValueChange={(e) => {
                options.editorCallback?.(e.value);
            }}
            onKeyDown={(e) => {
                e.stopPropagation();
            }}
        />
    );
};
